import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FileText, Plus, X, Loader2, Copy, Trash2, Download,
  Send, Eye, Edit3, BookOpen, Upload, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Percent,
  Building2, Hammer, FileSpreadsheet, ChevronRight, ChevronDown,
  AlertTriangle, History, ArrowRight, ListChecks,
  Ban, CalendarDays, Paperclip, FilePlus, Archive,
  RotateCcw, Users, TrendingUp, Target,
  Sparkles, BarChart3, PieChart as PieChartIcon, Wallet
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { WORKFLOW_STEPS, STATUS_META, NEXT_STEP_LABEL, TIPO_META, UNIDADES, DEFAULT_PCT, opts, fmt } from "./constants";
import { Modal, DeadlineBadge, WorkflowStepper, daysUntil, getWfIndex } from "./SharedComponents";

export function HorasHombreSection({ budgetId, budget, readOnly, onLiveChange }) {
  const fmtPyg = (n) => `Gs. ${Math.round(Number(n) || 0).toLocaleString("es-PY")}`;
  const [meses, setMeses] = useState(budget?.hh_meses_duracion ?? 24);
  const [totalHsMes, setTotalHsMes] = useState(budget?.hh_total_hs_mes || 180);
  const [hsLv, setHsLv] = useState(budget?.hh_hs_lv ?? 40);
  const [hsSab, setHsSab] = useState(budget?.hh_hs_sab ?? 5);
  const [hombresObra, setHombresObra] = useState(budget?.hombres_obra ?? 15);
  const [puntero, setPuntero] = useState(budget?.hh_puntero ?? 0);
  const [moCalif, setMoCalif] = useState(budget?.hh_mo_calificada?.length > 0 ? budget.hh_mo_calificada : [
    { cargo: "Ingeniero Residente", cantidad: 1, salario_mes: 0 },
    { cargo: "Chofer / Logística", cantidad: 1, salario_mes: 0 },
  ]);
  const [moNoCalif, setMoNoCalif] = useState(budget?.hh_mo_no_calificada?.length > 0 ? budget.hh_mo_no_calificada : [
    { cargo: "Ayudante Electricista", cantidad: 2, salario_mes: 0 },
  ]);
  const [moEsp, setMoEsp] = useState(budget?.hh_mo_especial?.length > 0 ? budget.hh_mo_especial : []);
  const [saving, setSaving] = useState(false);

  // H.H. calculations — modelo Excel CEN: hs_mensuales = hs_semanales × 4 (4 semanas/mes)
  const hsSemana = (parseFloat(hsLv) || 0) + (parseFloat(hsSab) || 0);
  const hsMensXHombre = hsSemana * 4;
  const totalHsHombreMes = (parseInt(hombresObra) || 1) * hsMensXHombre;
  // P1 Fix: Total HH = meses × (hombres × hs/hombre/mes) — antes usaba solo hs/hombre/mes sin multiplicar por hombres
  const totalHs = parseFloat(meses || 0) * totalHsHombreMes;
  const calcCosto = (rows) => rows.reduce((s, r) => s + (parseFloat(r.cantidad || 0) * parseFloat(r.salario_mes || 0) * parseFloat(meses || 1)), 0);
  const totalCosto = calcCosto(moCalif) + calcCosto(moNoCalif) + calcCosto(moEsp);

  // PDF REV1 A.1: notificar al padre cuando los valores HH cambian, para que GastosAdminDetalladoSection
  // pueda recalcular IPS / Seguro / EPI / Acta médica en tiempo real (sin necesidad de guardar).
  useEffect(() => {
    if (typeof onLiveChange === "function") {
      onLiveChange({
        meses: parseFloat(meses) || 0,
        hombres: parseInt(hombresObra) || 1,
        hsLv: parseFloat(hsLv) || 0,
        hsSab: parseFloat(hsSab) || 0,
        totalHH: totalHs,
        hsMensXHombre,
      });
    }
  }, [meses, hombresObra, hsLv, hsSab, totalHs, hsMensXHombre, onLiveChange]);

  const save = async () => {
    if (!budgetId) { toast.error("Guardá el presupuesto primero"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/budgets/${budgetId}`, {
        hh_meses_duracion: parseFloat(meses) || 0,
        hh_total_hs_mes: parseFloat(totalHsMes) || 180,
        hh_hs_lv: parseFloat(hsLv) || 40,
        hh_hs_sab: parseFloat(hsSab) || 5,
        hombres_obra: parseInt(hombresObra) || 1,
        hh_puntero: parseInt(puntero) || 0,
        hh_mo_calificada: moCalif,
        hh_mo_no_calificada: moNoCalif,
        hh_mo_especial: moEsp,
      }, { withCredentials: true });
      toast.success("Estimación de H.H. guardada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="horas-hombre-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center"><Users size={13} className="text-amber-600" /></div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">Estimación de Horas Hombre</h3>
            <p className="text-[11px] text-gray-400">Plantel, salarios y costo total de mano de obra por duración de obra</p>
          </div>
        </div>
        {!readOnly && (
          <button onClick={save} disabled={saving || !budgetId}
            className="flex items-center gap-1 text-xs text-white bg-amber-600 hover:bg-amber-700 px-3 py-1 rounded-lg disabled:opacity-50 transition-colors" data-testid="save-hh-btn">
            {saving ? <Loader2 size={12} className="animate-spin" /> : null}
            Guardar
          </button>
        )}
      </div>

      {/* Duración y H.H. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
        <div>
          <label className="block text-[10px] font-bold text-amber-700 mb-1">Duración obra (meses)</label>
          {readOnly ? (
            <p className="text-sm font-bold text-gray-800">{meses || "—"}</p>
          ) : (
            <input type="number" min="0" step="0.5" value={meses} onChange={e => setMeses(e.target.value)}
              className="w-full border border-amber-200 rounded-lg px-2 py-1 text-sm focus:ring-1 focus:ring-amber-300" data-testid="hh-meses-input" />
          )}
        </div>
        <div>
          <label className="block text-[10px] font-bold text-amber-700 mb-1">Hombres en obra</label>
          {readOnly ? (
            <p className="text-sm font-bold text-gray-800">{hombresObra}</p>
          ) : (
            <input type="number" min="1" value={hombresObra} onChange={e => setHombresObra(e.target.value)}
              className="w-full border border-amber-200 rounded-lg px-2 py-1 text-sm focus:ring-1 focus:ring-amber-300" data-testid="hombres-obra-input" />
          )}
        </div>
        <div>
          <label className="block text-[10px] font-bold text-amber-700 mb-1">Puntero (cant.)</label>
          {readOnly ? (
            <p className="text-sm font-bold text-gray-800">{puntero}</p>
          ) : (
            <input type="number" min="0" value={puntero} onChange={e => setPuntero(e.target.value)}
              className="w-full border border-amber-200 rounded-lg px-2 py-1 text-sm focus:ring-1 focus:ring-amber-300" data-testid="hh-puntero-input" />
          )}
        </div>
        <div>
          <label className="block text-[10px] font-bold text-amber-700 mb-1">Total Hs. Hombre (obra)</label>
          <p className="text-sm font-bold text-gray-800" data-testid="total-hh-value">{totalHs.toLocaleString("es-PY", { maximumFractionDigits: 0 })} hs.</p>
          <p className="text-[9px] text-amber-600">{parseInt(hombresObra)||1} hombres × {hsMensXHombre}hs/mes × {meses||0}m</p>
        </div>
      </div>
      {/* Hs. semanales desglose */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 p-3 bg-orange-50 rounded-xl border border-orange-100">
        <div>
          <label className="block text-[10px] font-bold text-orange-700 mb-1">Hs. semanales L-V</label>
          {readOnly ? <p className="text-sm font-bold text-gray-800">{hsLv} h</p> : (
            <input type="number" min="0" max="50" value={hsLv} onChange={e => setHsLv(e.target.value)}
              className="w-full border border-orange-200 rounded-lg px-2 py-1 text-sm focus:ring-1 focus:ring-orange-300" data-testid="hh-hs-lv-input" />
          )}
        </div>
        <div>
          <label className="block text-[10px] font-bold text-orange-700 mb-1">Hs. semanales Sábado</label>
          {readOnly ? <p className="text-sm font-bold text-gray-800">{hsSab} h</p> : (
            <input type="number" min="0" max="12" value={hsSab} onChange={e => setHsSab(e.target.value)}
              className="w-full border border-orange-200 rounded-lg px-2 py-1 text-sm focus:ring-1 focus:ring-orange-300" data-testid="hh-hs-sab-input" />
          )}
        </div>
        <div>
          <label className="block text-[10px] font-bold text-orange-700 mb-1">Hs. mens. × hombre</label>
          <p className="text-sm font-bold text-gray-800">{hsMensXHombre.toFixed(0)} h</p>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-orange-700 mb-1">Tot. Hs. Hombre / mes</label>
          <p className="text-sm font-bold text-orange-700">{totalHsHombreMes.toFixed(0)} h</p>
        </div>
      </div>

      <MoTable label="M.O. Calificada (Ing. / Técnico)" rows={moCalif} setRows={setMoCalif} color="text-blue-600" meses={meses} readOnly={readOnly} fmtPyg={fmtPyg} />
      <MoTable label="M.O. No Calificada (Ayudantes / Peones)" rows={moNoCalif} setRows={setMoNoCalif} color="text-gray-600" meses={meses} readOnly={readOnly} fmtPyg={fmtPyg} />
      <MoTable label="M.O. Especial / Otro" rows={moEsp} setRows={setMoEsp} color="text-purple-600" meses={meses} readOnly={readOnly} fmtPyg={fmtPyg} />

      <div className="border-t-2 border-amber-200 pt-3 flex items-center justify-between">
        <span className="text-xs font-bold text-amber-700">TOTAL COSTO MANO DE OBRA ({meses || 0} meses)</span>
        <span className="font-bold text-amber-700 font-mono text-sm">{fmtPyg(totalCosto)}</span>
      </div>
    </div>
  );
}


// ─── PDF REV1 B.1: MoTable EXTRAÍDO como componente top-level ──────────────────
// Antes era una closure DENTRO de HorasHombreSection, lo cual hacía que React lo
// tratara como un componente "nuevo" en cada render del padre → desmonta + remonta
// los inputs → se PIERDE el foco al tipear. Al moverlo afuera, React reconoce que
// es el mismo componente y preserva el foco.
function MoTable({ label, rows, setRows, color, meses, readOnly, fmtPyg }) {
  return (
    <div className="mb-4">
      <div className={`flex items-center justify-between mb-1.5`}>
        <p className={`text-[10px] uppercase font-bold tracking-wider ${color}`}>{label}</p>
        {!readOnly && (
          <button onClick={() => setRows(p => [...p, { cargo: "", cantidad: 1, salario_mes: 0 }])}
            className="text-[10px] text-gray-500 hover:text-gray-700 flex items-center gap-0.5 border border-gray-200 rounded px-1.5 py-0.5 hover:bg-gray-50">
            <Plus size={10} /> agregar
          </button>
        )}
      </div>
      <table className="w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Cargo / Categoría</th>
            <th className="px-2 py-1.5 text-center font-semibold text-gray-600 w-16">Cant.</th>
            <th className="px-2 py-1.5 text-right font-semibold text-gray-600 w-36">Salario / mes (Gs.)</th>
            <th className="px-2 py-1.5 text-right font-semibold text-gray-600 w-36">Costo Total</th>
            {!readOnly && <th className="w-6"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const costoRow = parseFloat(r.cantidad || 0) * parseFloat(r.salario_mes || 0) * parseFloat(meses || 1);
            return (
              <tr key={i} className="border-t border-gray-50">
                <td className="px-2 py-1">
                  {readOnly ? <span>{r.cargo}</span> : (
                    <input value={r.cargo} onChange={e => setRows(p => p.map((row, ri) => ri === i ? { ...row, cargo: e.target.value } : row))}
                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-xs focus:ring-1 focus:ring-blue-300" />
                  )}
                </td>
                <td className="px-2 py-1">
                  {readOnly ? <span className="text-center block">{r.cantidad}</span> : (
                    <input type="number" min="1" value={r.cantidad} onChange={e => setRows(p => p.map((row, ri) => ri === i ? { ...row, cantidad: e.target.value } : row))}
                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-xs text-center focus:ring-1 focus:ring-blue-300" />
                  )}
                </td>
                <td className="px-2 py-1">
                  {readOnly ? <span className="text-right block font-mono">{fmtPyg(r.salario_mes)}</span> : (
                    <input
                      type="text"
                      inputMode="numeric"
                      value={r.salario_mes === 0 || r.salario_mes === "0" ? "" : r.salario_mes}
                      placeholder="0"
                      onChange={e => {
                        const val = e.target.value.replace(/[^\d]/g, "");
                        setRows(p => p.map((row, ri) => ri === i ? { ...row, salario_mes: val === "" ? 0 : parseInt(val) } : row));
                      }}
                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-xs text-right focus:ring-1 focus:ring-blue-300"
                    />
                  )}
                </td>
                <td className="px-2 py-1 text-right font-mono text-gray-700">{fmtPyg(costoRow)}</td>
                {!readOnly && (
                  <td className="px-1 text-center">
                    <button onClick={() => setRows(p => p.filter((_, ri) => ri !== i))} className="text-red-400 hover:text-red-600"><X size={11} /></button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
